import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import warehouseService, { UpdateWarehouseDTO } from '../../services/warehouseService';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './WarehouseEditPage.css';
import { ArrowLeft, Save, Eye, Trash2 } from 'lucide-react';

// Max length constants for validation
const MAX_LENGTHS = {
  code: 10,
  name: 30,
  location: 100,
  address: 200,
  city: 50,
  state: 50,
  postalCode: 20,
};

interface FieldErrors {
  code?: string;
  name?: string;
  location?: string;
}

const WarehouseEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [warehouse, setWarehouse] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<UpdateWarehouseDTO>({
    code: '',
    name: '',
    location: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    isActive: true,
  });

  useEffect(() => {
    loadWarehouse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadWarehouse = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await warehouseService.getWarehouseById(parseInt(id || '0'));
      const warehouseData = response?.data?.data || response?.data;
      setWarehouse(warehouseData);
      setFormData({
        code: warehouseData.code,
        name: warehouseData.name,
        location: warehouseData.location,
        address: warehouseData.address || '',
        city: warehouseData.city || '',
        state: warehouseData.state || '',
        postalCode: warehouseData.postalCode || '',
        isActive: warehouseData.isActive,
      });
      setLoading(false);
    } catch (err) {
      setError(t('warehouse.failedToLoadWarehouse'));
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    const checkboxTarget = e.target as HTMLInputElement;

    let finalValue: any;
    if (isCheckbox) {
      finalValue = checkboxTarget.checked;
    } else {
      // Enforce max length by truncating
      finalValue = value;
      if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
        finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // Clear field error when user starts typing
    if (!isCheckbox) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Validate Code
    if (!formData.code.trim()) {
      errors.code = 'Warehouse code is required';
      isValid = false;
    } else if (formData.code.length > MAX_LENGTHS.code) {
      errors.code = `Warehouse code cannot exceed ${MAX_LENGTHS.code} characters`;
      isValid = false;
    }

    // Validate Name
    if (!formData.name.trim()) {
      errors.name = 'Warehouse name is required';
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = `Warehouse name cannot exceed ${MAX_LENGTHS.name} characters`;
      isValid = false;
    }

    // Validate Location
    if (!formData.location.trim()) {
      errors.location = 'Location is required';
      isValid = false;
    } else if (formData.location.length > MAX_LENGTHS.location) {
      errors.location = `Location cannot exceed ${MAX_LENGTHS.location} characters`;
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm() || !warehouse) {
      return;
    }

    setSaving(true);
    try {
      await warehouseService.updateWarehouse(warehouse.id, formData);
      navigate('/organization/warehouses', {
        state: { message: t('warehouse.updateSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('warehouse.failedToUpdateWarehouse')).message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="edit-loading-container">
          <div className="edit-spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!warehouse) {
    return (
      <Layout>
        <div className="edit-container">
          <div className="alert alert-danger">{t('warehouse.failedToLoadWarehouse')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container">
        {/* Navigation Breadcrumb */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/organization/warehouses')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              {t('warehouse.editTitle')}
              <span className="ims-page-subtitle">{warehouse.name}</span>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/organization/warehouses')} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.saveChanges')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
             <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          {/* Form Card */}
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('warehouse.details')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">
                      {t('warehouse.code')} <span className="required">*</span>
                      <span className="char-counter">{formData.code.length}/{MAX_LENGTHS.code}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.code ? 'is-invalid' : ''}`}
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder={t('warehouse.code')}
                      maxLength={MAX_LENGTHS.code}
                    />
                    {fieldErrors.code && <small className="form-error">{fieldErrors.code}</small>}
                    {!fieldErrors.code && <small className="form-helper">Unique identifier for the warehouse</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('warehouse.warehouseName')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('warehouse.warehouseName')}
                      maxLength={MAX_LENGTHS.name}
                    />
                    {fieldErrors.name && <small className="form-error">{fieldErrors.name}</small>}
                    {!fieldErrors.name && <small className="form-helper">e.g., Main Warehouse, Secondary Storage</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('warehouse.location')} <span className="required">*</span>
                      <span className="char-counter">{formData.location.length}/{MAX_LENGTHS.location}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.location ? 'is-invalid' : ''}`}
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder={t('warehouse.location')}
                      maxLength={MAX_LENGTHS.location}
                    />
                    {fieldErrors.location && <small className="form-error">{fieldErrors.location}</small>}
                    {!fieldErrors.location && <small className="form-helper">Primary location of the warehouse</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('warehouse.address')}
                      <span className="char-counter">{formData.address.length}/{MAX_LENGTHS.address}</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder={t('warehouse.address')}
                      maxLength={MAX_LENGTHS.address}
                    />
                    <small className="form-helper">Street address (optional)</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('warehouse.city')}
                        <span className="char-counter">{formData.city.length}/{MAX_LENGTHS.city}</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder={t('warehouse.city')}
                        maxLength={MAX_LENGTHS.city}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('warehouse.state')}
                        <span className="char-counter">{formData.state.length}/{MAX_LENGTHS.state}</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder={t('warehouse.state')}
                        maxLength={MAX_LENGTHS.state}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('warehouse.postalCode')}
                        <span className="char-counter">{formData.postalCode.length}/{MAX_LENGTHS.postalCode}</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder={t('warehouse.postalCode')}
                        maxLength={MAX_LENGTHS.postalCode}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        style={{ marginRight: '8px' }}
                      />
                      {t('common.active')}
                    </label>
                  </div>

                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="edit-sidebar">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.info')}</h3>
              </div>
              <div className="edit-card-body">
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                  <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                    <p style={{ margin: '4px 0' }}>
                      <strong>{t('common.status')}:</strong>{' '}
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          backgroundColor: formData.isActive ? '#d1fae5' : '#fee2e2',
                          color: formData.isActive ? '#065f46' : '#991b1b',
                          fontSize: '11px',
                          fontWeight: '500',
                        }}
                      >
                        {formData.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </p>
                  </div>

                  <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                    <p style={{ margin: '4px 0' }}>
                      <strong>ID:</strong>
                    </p>
                    <p
                      style={{
                        margin: '4px 0',
                        padding: '6px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        wordBreak: 'break-all',
                      }}
                    >
                      {warehouse.id}
                    </p>
                  </div>

                  <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                    <p style={{ margin: '4px 0' }}>
                      <strong>{t('warehouse.code')}:</strong>
                    </p>
                    <p
                      style={{
                        margin: '4px 0',
                        padding: '6px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                      }}
                    >
                      {warehouse.code}
                    </p>
                  </div>

                  <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                    <p style={{ margin: '4px 0' }}>
                      <strong>{t('common.createdDate')}:</strong>
                    </p>
                    <p style={{ margin: '4px 0' }}>
                      {new Date(warehouse.createdAt).toLocaleDateString(
                        window.navigator.language === 'ja' ? 'ja-JP' : 'en-US'
                      )}{' '}
                      {new Date(warehouse.createdAt).toLocaleTimeString(
                        window.navigator.language === 'ja' ? 'ja-JP' : 'en-US',
                        { hour: '2-digit', minute: '2-digit' }
                      )}
                    </p>
                  </div>

                  {warehouse.modifiedAt && (
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                      <p style={{ margin: '4px 0' }}>
                        <strong>{t('common.modifiedDate')}:</strong>
                      </p>
                      <p style={{ margin: '4px 0' }}>
                        {new Date(warehouse.modifiedAt).toLocaleDateString(
                          window.navigator.language === 'ja' ? 'ja-JP' : 'en-US'
                        )}{' '}
                        {new Date(warehouse.modifiedAt).toLocaleTimeString(
                          window.navigator.language === 'ja' ? 'ja-JP' : 'en-US',
                          { hour: '2-digit', minute: '2-digit' }
                        )}
                      </p>
                    </div>
                  )}

                  <div style={{ marginBottom: '0' }}>
                    <p style={{ margin: '4px 0' }}>
                      <strong>{t('common.location')}:</strong>
                    </p>
                    <p style={{ margin: '4px 0', color: '#374151' }}>
                      {warehouse.location}
                      {warehouse.city && `, ${warehouse.city}`}
                      {warehouse.state && `, ${warehouse.state}`}
                      {warehouse.postalCode && ` ${warehouse.postalCode}`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Actions Card */}
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.quickActions')}</h3>
              </div>
              <div className="edit-card-body">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => navigate(`/organization/warehouses/${warehouse.id}`)}
                  style={{ width: '100%', marginBottom: '8px', minWidth: '100px' }}
                >
                  <Eye size={14} strokeWidth={2} style={{ marginRight: '6px', display: 'inline' }} />
                  {t('common.view')}
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => navigate(`/organization/warehouses/${warehouse.id}/delete`)}
                  style={{
                    width: '100%',
                    minWidth: '100px',
                    color: '#dc2626',
                    borderColor: '#fecaca',
                    backgroundColor: '#fef2f2',
                  }}
                >
                  <Trash2 size={14} strokeWidth={2} style={{ marginRight: '6px', display: 'inline' }} />
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WarehouseEditPage;
